import React, { useState, useEffect } from "react";
import embelem from "../../assets/embelem.png";

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  mobileNumber: string;
  city: string;
  message: string;
}

interface ValidationErrors {
  name?: string;
  mobileNumber?: string;
  city?: string;
  message?: string;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    mobileNumber: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

  // Focus management for accessibility
  useEffect(() => {
    if (isOpen) {
      // Focus first input when modal opens
      const firstInput = document.querySelector(
        'input[name="name"]'
      ) as HTMLInputElement;
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }
  }, [isOpen]);



  // Client-side validation
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    } else if (formData.name.trim().length > 50) {
      errors.name = "Name cannot exceed 50 characters";
    } else if (!/^[a-zA-Z\s.'-]+$/.test(formData.name.trim())) {
      errors.name =
        "Name can only contain letters, spaces, dots, hyphens and apostrophes";
    }

    // Mobile number validation
    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = "Mobile number is required";
    } else {
      const cleanNumber = formData.mobileNumber.replace(/[\s\-()]/g, "");
      if (!/^[+]?[1-9]\d{9,14}$/.test(cleanNumber)) {
        errors.mobileNumber = "Please enter a valid mobile number";
      }
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = "City is required";
    } else if (formData.city.trim().length < 2) {
      errors.city = "City must be at least 2 characters";
    } else if (formData.city.trim().length > 30) {
      errors.city = "City cannot exceed 30 characters";
    } else if (!/^[a-zA-Z\s.'-]+$/.test(formData.city.trim())) {
      errors.city =
        "City can only contain letters, spaces, dots, hyphens and apostrophes";
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters";
    } else if (formData.message.trim().length > 500) {
      errors.message = "Message cannot exceed 500 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setValidationErrors({});

    // Client-side validation
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(
          result.message ||
            "Contact form submitted successfully! We will get back to you soon."
        );
        setFormData({ name: "", mobileNumber: "", city: "", message: "" });
        setHasUnsavedChanges(false);
        setTimeout(() => onClose(), 2500);
      } else {
        // Handle validation errors from server
        if (result.errors && Array.isArray(result.errors)) {
          const serverErrors: ValidationErrors = {};
          result.errors.forEach((err: any) => {
            if (err.path) {
              serverErrors[err.path as keyof ValidationErrors] = err.msg;
            }
          });
          setValidationErrors(serverErrors);
        } else {
          setError(result.message || "Something went wrong. Please try again.");
        }
      }
    } catch (err) {
      console.error("Network error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setHasUnsavedChanges(true);

    // Clear validation error for this field when user starts typing
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges && !success) {
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to close?"
      );
      if (!confirmClose) return;
    }

    // Reset form state
    setFormData({ name: "", mobileNumber: "", city: "", message: "" });
    setError("");
    setSuccess("");
    setValidationErrors({});
    setHasUnsavedChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl mx-4 md:mx-auto flex flex-col md:flex-row overflow-hidden">
        {/* Left Image */}
        <div className="w-full md:w-1/2 bg-[#622935] flex items-center justify-center p-8">
          <img
            src={embelem}
            alt="AWM Emblem"
            className="max-w-[100%] h-auto object-contain"
          />
        </div>

        {/* Right Form */}
        <div className="w-full md:w-1/2 p-6 md:p-10 relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-black text-2xl focus:outline-none focus:ring-2 focus:ring-[#622935] rounded"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <h2
            id="modal-title"
            className="text-xl font-medium mb-6 font-display"
          >
            CONTACT US
          </h2>

          {error && (
            <div
              className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded"
              role="alert"
            >
              {error}
            </div>
          )}

          {success && (
            <div
              className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded"
              role="alert"
            >
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Name *"
                required
                aria-required="true"
                aria-invalid={!!validationErrors.name}
                aria-describedby={
                  validationErrors.name ? "name-error" : undefined
                }
                className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition font-body ${
                  validationErrors.name
                    ? "border-[#622935] focus:ring-[#622935]"
                    : "border-gray-300 focus:ring-[#622935] font-body"
                }`}
              />
              {validationErrors.name && (
                <p
                  id="name-error"
                  className="mt-1 text-sm text-[#622935] font-body"
                  role="alert"
                >
                  {validationErrors.name}
                </p>
              )}
            </div>

            <div>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                placeholder="Mobile Number *"
                required
                aria-required="true"
                aria-invalid={!!validationErrors.mobileNumber}
                aria-describedby={
                  validationErrors.mobileNumber ? "mobile-error" : undefined
                }
                className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition ${
                  validationErrors.mobileNumber
                    ? "border-[#622935] focus:ring-[#622935]"
                    : "border-gray-300 focus:ring-[#622935] font-body"
                }`}
              />
              {validationErrors.mobileNumber && (
                <p
                  id="mobile-error"
                  className="mt-1 text-sm text-[#622935] font-body"
                  role="alert"
                >
                  {validationErrors.mobileNumber}
                </p>
              )}
            </div>

            <div>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Your City *"
                required
                aria-required="true"
                aria-invalid={!!validationErrors.city}
                aria-describedby={
                  validationErrors.city ? "city-error" : undefined
                }
                className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition ${
                  validationErrors.city
                    ? "border-[#622935] focus:ring-[#622935]"
                    : "border-gray-300 focus:ring-[#622935] font-body"
                }`}
              />
              {validationErrors.city && (
                <p
                  id="city-error"
                  className="mt-1 text-sm text-[#622935] font-body"
                  role="alert"
                >
                  {validationErrors.city}
                </p>
              )}
            </div>

            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Message (minimum 10 characters) *"
                rows={3}
                required
                aria-required="true"
                aria-invalid={!!validationErrors.message}
                aria-describedby={
                  validationErrors.message ? "message-error" : undefined
                }
                className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition resize-vertical ${
                  validationErrors.message
                    ? "border-[#622935] focus:ring-[#622935]"
                    : "border-gray-300 focus:ring-[#622935] font-body"
                }`}
              />
              {validationErrors.message && (
                <p
                  id="message-error"
                  className="mt-1 text-sm text-[#622935] font-body"
                  role="alert"
                >
                  {validationErrors.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.message.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#622935] text-white py-2 rounded-md font-medium hover:bg-[#4A1F28] transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#622935] focus:ring-offset-2 font-body"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  SUBMITTING...
                </span>
              ) : (
                "SUBMIT"
              )}
            </button>
          </form>

          <p className="mt-4 text-xs text-gray-500 text-center font-body">
            * Required fields
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;
